import React from "react";
import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { useT } from "@/context/LanguageContext";
import { ArrowRight, MapPin } from "@phosphor-icons/react";

export function CaseTeaser({ studies = [] }) {
  const { t } = useT();
  if (!studies.length) return null;

  return (
    <section className="bg-mist py-24 lg:py-32" data-testid="case-teaser-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline={t("caseStudies.overline")}
          title={t("caseStudies.title")}
          subtitle={t("caseStudies.subtitle")}
          align="center"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {studies.slice(0, 3).map((cs, i) => (
            <Reveal key={cs.id} delay={i * 0.08}>
              <Link
                to={`/case-studies/${cs.slug}`}
                data-testid={`case-teaser-card-${cs.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-teal/50 hover:shadow-xl"
              >
                <div className="h-40 overflow-hidden">
                  <img src={cs.cover_image} alt={cs.title} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-2 text-[11px] font-semibold">
                    <span className="rounded bg-teal/10 px-2 py-0.5 text-teal">{cs.industry}</span>
                    <span className="flex items-center gap-1 text-slate-400"><MapPin size={12} /> {cs.country}</span>
                  </div>
                  <h3 className="mt-3 flex-1 font-heading text-base font-semibold leading-snug text-ink">{cs.title}</h3>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                    {(cs.metrics || [])[0] && (
                      <div>
                        <p className="font-heading text-xl font-bold text-teal">{cs.metrics[0].value}</p>
                        <p className="text-[10px] text-slate-400">{cs.metrics[0].label}</p>
                      </div>
                    )}
                    <span className="flex items-center gap-1 text-sm font-semibold text-teal">
                      {t("caseStudies.read")} <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/case-studies" data-testid="case-teaser-viewall"
            className="inline-flex items-center gap-2 rounded-md border border-ink px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white">
            {t("caseStudies.back")} <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}
